// Consolidated Realtime Subscription Manager
// Purpose: Reduce database load by sharing subscriptions and batching updates
// This replaces individual component subscriptions with a shared singleton approach

import { ref, reactive, computed, watch } from 'vue'
import { supabase } from '@/lib/supabase'

// Singleton state for shared subscriptions
let subscriptionManager = null

const createSubscriptionManager = () => {
  // Connection state
  const isConnected = ref(false)
  const isTabVisible = ref(true)
  const lastActivity = ref(Date.now())

  // Subscription registry - tracks which components are interested in which tables
  const subscribers = reactive({
    payments: new Set(),
    orders: new Set(), 
    quotations: new Set(),
    expenses: new Set(),
    order_status_history: new Set()
  })

  // Active subscriptions
  const activeSubscriptions = reactive({})
  
  // Batch update system to reduce redundant API calls
  const pendingUpdates = reactive(new Set())
  let updateTimer = null
  const UPDATE_BATCH_DELAY = 1000 // 1 second delay to batch multiple updates

  // Tab visibility handling
  const handleVisibilityChange = () => {
    isTabVisible.value = !document.hidden
    if (isTabVisible.value) {
      lastActivity.value = Date.now()
      console.log('[ConsolidatedRealtime] Tab became visible - resuming active monitoring')
    }
  }

  // Initialize visibility tracking
  document.addEventListener('visibilitychange', handleVisibilityChange)

  // Batch update executor
  const executeBatchedUpdates = () => {
    if (pendingUpdates.size === 0) return

    console.log('[ConsolidatedRealtime] Executing batched updates for tables:', Array.from(pendingUpdates))
    
    // Notify all subscribers of the batched changes
    pendingUpdates.forEach(tableName => {
      const tableSubscribers = subscribers[tableName]
      if (tableSubscribers) {
        tableSubscribers.forEach(callback => {
          try {
            callback({ 
              type: 'batch_update', 
              table: tableName,
              timestamp: Date.now()
            })
          } catch (error) {
            console.error(`[ConsolidatedRealtime] Error in subscriber callback for ${tableName}:`, error)
          }
        })
      }
    })

    pendingUpdates.clear()
    updateTimer = null
  }

  // Add update to batch
  const scheduleUpdate = (tableName) => {
    pendingUpdates.add(tableName)
    
    // Clear existing timer and set a new one
    if (updateTimer) {
      clearTimeout(updateTimer)
    }
    
    updateTimer = setTimeout(executeBatchedUpdates, UPDATE_BATCH_DELAY)
  }

  // Create or reuse subscription for a table
  const ensureSubscription = async (tableName) => {
    if (activeSubscriptions[tableName]) {
      console.log(`[ConsolidatedRealtime] Reusing existing subscription for ${tableName}`)
      return activeSubscriptions[tableName]
    }

    console.log(`[ConsolidatedRealtime] Creating new subscription for ${tableName}`)
    
    const subscription = supabase
      .channel(`consolidated-${tableName}`)
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: tableName 
        },
        (payload) => {
          console.log(`[ConsolidatedRealtime] Change detected in ${tableName}:`, payload.eventType)
          
          // Only process if tab is visible to reduce unnecessary work
          if (isTabVisible.value) {
            scheduleUpdate(tableName)
          } else {
            console.log(`[ConsolidatedRealtime] Tab not visible, deferring update for ${tableName}`)
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          isConnected.value = true
          console.log(`[ConsolidatedRealtime] Successfully subscribed to ${tableName}`)
        } else if (status === 'CHANNEL_ERROR') {
          isConnected.value = false
          console.error(`[ConsolidatedRealtime] Subscription error for ${tableName}`)
        } else if (status === 'CLOSED') {
          isConnected.value = false
          console.log(`[ConsolidatedRealtime] Subscription closed for ${tableName}`)
        }
      })

    activeSubscriptions[tableName] = subscription
    return subscription
  }

  // Subscribe a component to table changes
  const subscribe = async (tableName, callback, componentId) => {
    if (!subscribers[tableName]) {
      subscribers[tableName] = new Set()
    }

    // Add callback with component identifier
    const callbackWrapper = (payload) => {
      try {
        callback(payload)
      } catch (error) {
        console.error(`[ConsolidatedRealtime] Error in callback for ${componentId}:`, error)
      }
    }
    
    callbackWrapper._componentId = componentId
    subscribers[tableName].add(callbackWrapper)

    // Ensure subscription exists
    await ensureSubscription(tableName)

    console.log(`[ConsolidatedRealtime] Added subscriber ${componentId} to ${tableName}. Total subscribers: ${subscribers[tableName].size}`)

    // Return unsubscribe function
    return () => {
      subscribers[tableName].delete(callbackWrapper)
      console.log(`[ConsolidatedRealtime] Removed subscriber ${componentId} from ${tableName}. Remaining: ${subscribers[tableName].size}`)
      
      // Clean up subscription if no more subscribers
      if (subscribers[tableName].size === 0) {
        if (activeSubscriptions[tableName]) {
          console.log(`[ConsolidatedRealtime] Cleaning up unused subscription for ${tableName}`)
          activeSubscriptions[tableName].unsubscribe()
          delete activeSubscriptions[tableName]
        }
      }
    }
  }

  // Batch subscribe to multiple tables for a component
  const subscribeBatch = async (tableConfigs, componentId) => {
    const unsubscribeFns = []
    
    for (const { table, callback } of tableConfigs) {
      const unsubscribe = await subscribe(table, callback, componentId)
      unsubscribeFns.push(unsubscribe)
    }

    // Return batch unsubscribe function
    return () => {
      unsubscribeFns.forEach(fn => fn())
    }
  }

  // Get connection statistics
  const getStats = () => {
    const totalSubscribers = Object.values(subscribers)
      .reduce((sum, set) => sum + set.size, 0)
    
    return {
      isConnected: isConnected.value,
      isTabVisible: isTabVisible.value,
      activeSubscriptions: Object.keys(activeSubscriptions).length,
      totalSubscribers,
      subscribersByTable: Object.fromEntries(
        Object.entries(subscribers).map(([table, set]) => [table, set.size])
      ),
      lastActivity: lastActivity.value
    }
  }

  // Cleanup function
  const cleanup = () => {
    // Clear any pending timers
    if (updateTimer) {
      clearTimeout(updateTimer)
      updateTimer = null
    }

    // Unsubscribe from all subscriptions
    Object.values(activeSubscriptions).forEach(subscription => {
      subscription.unsubscribe()
    })

    // Clear state
    Object.keys(activeSubscriptions).forEach(key => {
      delete activeSubscriptions[key]
    })
    
    Object.keys(subscribers).forEach(key => {
      subscribers[key].clear()
    })

    // Remove event listeners
    document.removeEventListener('visibilitychange', handleVisibilityChange)

    console.log('[ConsolidatedRealtime] Cleanup completed')
  }

  return {
    isConnected: computed(() => isConnected.value),
    isTabVisible: computed(() => isTabVisible.value),
    subscribe,
    subscribeBatch,
    getStats,
    cleanup
  }
}

// Export singleton composable
export const useConsolidatedRealtime = () => {
  if (!subscriptionManager) {
    subscriptionManager = createSubscriptionManager()
  }
  return subscriptionManager
} 